import React, { createContext, ReactNode, useCallback, useContext } from 'react';

import { useModal } from '@futrli/shared-services';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { CreateDriverGroupSidebar } from '../drawers/group/create-driver-group-sidebar';
import { DeleteDriverModal } from '../modals/delete-driver-modal';
import { DriversExcelUpload } from '../excel-upload/drivers-excel-upload';
import { EditDriverGroupSidebar } from '../drawers/group/edit-driver-group-sidebar';
import { NonFinancialDriverTreeNode } from '../../types';
import { useDriversView } from './drivers-view-context';
import { useOrgRouteParams } from '../../../hooks/useOrgRouteParams/useOrgRouteParams';

interface DriverActionsContextValue {
  handleCreateDriver: () => void;
  handleEditDriver: (row: NonFinancialDriverTreeNode) => void;
  handleDeleteDriver: (row: NonFinancialDriverTreeNode) => void;
  handleDuplicateDriver: (row: NonFinancialDriverTreeNode) => void;
  handleExcelUpload: () => void;
  handleCreateGroup: () => void;
  handleEditGroup: (row: NonFinancialDriverTreeNode) => void;
  handleDeleteGroup: (row: NonFinancialDriverTreeNode) => void;
}

const DriverActionsContext = createContext<DriverActionsContextValue | undefined>(undefined);

export const DriverActionsProvider = ({ children }: { children: ReactNode }) => {
  const { driverBuilder } = useSidebarContext();
  const { duplicateDriver } = useDriversView();
  const { organisationId, workspaceId } = useOrgRouteParams();

  const deleteDriverDialog = useModal();
  const deleteGroupDialog = useModal();
  const createGroupSidebar = useModal();
  const editGroupSidebar = useModal();
  const excelUploadSidebar = useModal();

  const [driverRow, setDriverRow] = React.useState<NonFinancialDriverTreeNode>();
  const [groupRow, setGroupRow] = React.useState<NonFinancialDriverTreeNode>();

  const handleCreateDriver = useCallback(() => {
    driverBuilder.open({});
  }, [driverBuilder]);

  const handleEditDriver = useCallback(
    (row: NonFinancialDriverTreeNode) => {
      driverBuilder.open({ driverId: row.id });
    },
    [driverBuilder]
  );

  const handleDeleteDriver = useCallback(
    (row: NonFinancialDriverTreeNode) => {
      setDriverRow(row);
      deleteDriverDialog.open();
    },
    [deleteDriverDialog]
  );

  const handleDuplicateDriver = useCallback(
    (row: NonFinancialDriverTreeNode) => {
      duplicateDriver(row.id, { name: `${row.name} (copy)` });
    },
    [duplicateDriver]
  );

  const handleExcelUpload = useCallback(() => {
    excelUploadSidebar.open();
  }, [excelUploadSidebar]);

  const handleExcelUploadSuccess = useCallback(async () => {
    excelUploadSidebar.close();
  }, [excelUploadSidebar]);

  const handleCreateGroup = useCallback(() => {
    createGroupSidebar.open();
  }, [createGroupSidebar]);

  const handleEditGroup = useCallback(
    (row: NonFinancialDriverTreeNode) => {
      setGroupRow(row);
      editGroupSidebar.open();
    },
    [editGroupSidebar]
  );

  const handleDeleteGroup = useCallback(
    (row: NonFinancialDriverTreeNode) => {
      setGroupRow(row);
      deleteGroupDialog.open();
    },
    [deleteGroupDialog]
  );

  const value = {
    handleCreateDriver,
    handleEditDriver,
    handleDeleteDriver,
    handleDuplicateDriver,
    handleExcelUpload,
    handleCreateGroup,
    handleEditGroup,
    handleDeleteGroup
  };

  return (
    <DriverActionsContext.Provider value={value}>
      {driverRow && deleteDriverDialog.isOpen && (
        <DeleteDriverModal row={driverRow} onClose={deleteDriverDialog.close} isOpen />
      )}
      {groupRow && deleteGroupDialog.isOpen && (
        <DeleteDriverModal row={groupRow} onClose={deleteGroupDialog.close} isOpen />
      )}
      {excelUploadSidebar.isOpen && (
        <DriversExcelUpload
          workspaceId={workspaceId}
          organisationId={organisationId}
          isOpen
          handleClose={excelUploadSidebar.close}
          onSuccess={handleExcelUploadSuccess}
        />
      )}
      <CreateDriverGroupSidebar isOpen={createGroupSidebar.isOpen} onClose={createGroupSidebar.close} />
      {groupRow && (
        <EditDriverGroupSidebar row={groupRow} isOpen={editGroupSidebar.isOpen} onClose={editGroupSidebar.close} />
      )}
      {children}
    </DriverActionsContext.Provider>
  );
};

export const useDriverActions = () => {
  const context = useContext(DriverActionsContext);
  if (!context) {
    throw new Error('useDriverActions must be used within a DriverActionsProvider');
  }
  return context;
};
